import { Progress } from "@/core/components/ui/progress";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import { DocumentIconFor } from "./document-icon";
import {
  UPLOAD_ITEM_STATE,
  type UploadItem,
  type UploadItemState,
} from "./types";

// Uploads start the moment files are chosen, so this is not a queue waiting on a button:
// it is the same list as the documents below it, one step earlier. The rows match, so the
// two read as one list where the top few are still arriving.
const stateLabel = (status: UploadItemState) => {
  switch (status.state) {
    case UPLOAD_ITEM_STATE.uploading:
      return `${status.progress}%`;
    case UPLOAD_ITEM_STATE.skipped:
      return "Skipped";
    case UPLOAD_ITEM_STATE.failed:
      return "Could not send";
    case UPLOAD_ITEM_STATE.done:
      return "Sent";
    default:
      return "Waiting";
  }
};

interface UploadProgressProps {
  items: UploadItem[];
}

export const UploadProgress = ({ items }: UploadProgressProps) => (
  <Stack gap="none" align="stretch" fullWidth role="status">
    {items.map(({ file, status }, index) => {
      const failed = status.state === UPLOAD_ITEM_STATE.failed;

      return (
        <Stack
          key={`${file.name}-${index}`}
          gap="xs"
          align="stretch"
          fullWidth
          className="border-b border-stroke-tertiary py-3 last:border-b-0"
        >
          <Stack direction="row" gap="sm" align="center" fullWidth>
            <DocumentIconFor
              fileName={file.name}
              className="h-5 w-5 shrink-0 text-tertiary-text"
            />
            <Typography variant="bodyW2" className="min-w-0 flex-1 truncate">
              {file.name}
            </Typography>
            <Typography
              variant="captionW1"
              className={cn({
                "text-destructive": failed,
                "text-tertiary-text": !failed,
              })}
            >
              {stateLabel(status)}
            </Typography>
          </Stack>
          {status.state === UPLOAD_ITEM_STATE.uploading && (
            <Progress
              value={status.progress}
              className="h-1 bg-stroke-tertiary [&>div]:!bg-primary [&>div]:!bg-none"
            />
          )}
          {failed && (
            <Typography variant="captionW1" className="text-destructive">
              {status.message}
            </Typography>
          )}
        </Stack>
      );
    })}
  </Stack>
);
